import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, FileText, TrendingUp, Trophy, Users, Download } from 'lucide-react';
import { formatDate, formatDateForInput } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SalesOverviewComponent = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [error, setError] = useState('');
  const [salesmanNames, setSalesmanNames] = useState({});

  const initializeDateRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setStartDate(formatDate(sevenDaysAgo));
    setEndDate(formatDate(today));
  };

  useEffect(() => {
    initializeDateRange();
  }, []);

  const getDateRange = () => {
    return {
      start_date: startDate,
      end_date: endDate
    };
  };

  const fetchSalesmanName = async (salesmanCode) => {
    try {
      const cleanCode = salesmanCode.trim();
      const response = await fetch(`https://apisaleeasy.psisat.com/api/salesman/code/${cleanCode}`);
      
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.success ? data.data.SalesmanName : null;
    } catch (err) {
      console.error('Error fetching salesman name:', err);
      return null;
    }
  };

  const fetchSalesAnalytics = async () => {
    setLoading(true);
    setError('');
    setSalesData(null);
    setSalesmanNames({});

    try {
      const dateRange = getDateRange();
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          group_by: "month"
        },
        filters: {
          company_code: "045"
        }
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/salesman-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status && data.data) {
        setSalesData(data.data);
        
        // Fetch salesman names for all salesmen
        if (data.data.salesmen) {
          const namesPromises = data.data.salesmen.map(async (salesman) => {
            const name = await fetchSalesmanName(salesman.salesman_code);
            return { code: salesman.salesman_code, name };
          });
          
          const namesResults = await Promise.all(namesPromises);
          const namesMap = {};
          namesResults.forEach(result => {
            if (result.name) {
              namesMap[result.code] = result.name;
            }
          });
          setSalesmanNames(namesMap);
        }
      } else {
        throw new Error(data.result_desc || 'ไม่สามารถดึงข้อมูลได้');
      }
    } catch (err) {
      console.error('Error fetching sales analytics:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0) + ' บาท';
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-600 bg-yellow-100';
    if (rank === 2) return 'text-gray-600 bg-gray-100';
    if (rank === 3) return 'text-orange-600 bg-orange-100';
    if (rank <= 10) return 'text-blue-600 bg-blue-100';
    return 'text-gray-500 bg-gray-50';
  };

  const exportToExcel = () => {
    if (!salesData || !salesData.salesmen) {
      alert('ไม่มีข้อมูลสำหรับ export');
      return;
    }

    const toRow = (salesman, rank) => ({
      'อันดับ': rank ?? salesman.rank,
      'ทีม': salesman.team_name || '-',
      'รหัสเซลล์แมน': salesman.salesman_code,
      'ชื่อเซลล์แมน': salesmanNames[salesman.salesman_code] || '-',
      'ยอดขาย (บาท)': parseFloat(salesman.total_amount_before_vat || 0),
      'จำนวนบิล': salesman.total_bills,
      'วันที่บิลแรก': formatDate(salesman.first_bill_date),
      'วันที่บิลล่าสุด': formatDate(salesman.last_bill_date)
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesData.salesmen.map(s => toRow(s))), 'รายละเอียดเซลล์แมน');

    const fileName = `รายงานพนักงานขาย_${startDate}_ถึง_${endDate}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">พนักงานขาย PSI Smart Sale</h1>
              <p className="text-gray-600">ดูสรุปยอดขายและอันดับเซลล์แมนตามช่วงวันที่</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">เลือกข้อมูลที่ต้องการดู</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่เริ่มต้น</label>
                <MaskedDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholderText="DD/MM/YYYY"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่สิ้นสุด</label>
                <MaskedDatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholderText="DD/MM/YYYY"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={fetchSalesAnalytics}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
              >
                {loading ? 'กำลังโหลด...' : 'ดูข้อมูล'}
              </button>
              
              {salesData && salesData.salesmen && (
                <button
                  onClick={exportToExcel}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  ออก Excel
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                {error}
              </div>
            )}
          </div>

          {/* Results */}
          {salesData && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">ยอดขายรวม</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(salesData.summary?.total_amount_before_vat)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">จำนวนบิลรวม</p>
                      <p className="text-xl font-bold text-gray-900">
                        {salesData.summary?.total_bills?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">จำนวนเซลล์แมน</p>
                      <p className="text-xl font-bold text-gray-900">
                        {salesData.summary?.total_salesmen || '0'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Period Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">ข้อมูลช่วงเวลา</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">ช่วงเวลา</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(salesData.period?.start_date)} - {formatDate(salesData.period?.end_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">เวลาที่ดึงข้อมูล</p>
                    <p className="font-medium text-gray-900">
                      {new Date(salesData.metadata?.generated_at).toLocaleString('th-TH')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Top 10 Salesmen */}
              {salesData.salesmen && salesData.salesmen.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    อันดับเซลล์แมนยอดขายสูงสุด (Top 10)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-base">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-center py-3 px-4 font-medium text-gray-600">อันดับ</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">เซลล์แมน</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">ทีม</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600">ยอดขาย</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">จำนวนบิล</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">บิลแรก</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">บิลล่าสุด</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.salesmen.slice(0, 10).map((salesman, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getRankColor(salesman.rank)}`}>
                                {salesman.rank}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-medium text-gray-900 text-base">{salesman.salesman_code}</div>
                                {salesmanNames[salesman.salesman_code] && (
                                  <div className="text-sm text-gray-500">{salesmanNames[salesman.salesman_code]}</div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">{salesman.team_name || '-'}</td>
                            <td className="py-3 px-4 text-right font-medium text-gray-900 text-base">
                              {formatCurrency(salesman.total_amount_before_vat)}
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600 text-base">
                              {salesman.total_bills}
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600 text-sm">
                              {formatDate(salesman.first_bill_date)}
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600 text-sm">
                              {formatDate(salesman.last_bill_date)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* All Salesmen List */}
              {salesData.salesmen && salesData.salesmen.length > 10 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      รายการเซลล์แมนทั้งหมด ({salesData.salesmen.length} คน)
                    </h3>
                    <button
                      onClick={exportToExcel}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      ออก Excel
                    </button>
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full text-base">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-gray-200">
                          <th className="text-center py-3 px-4 font-medium text-gray-600">อันดับ</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">เซลล์แมน</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">ทีม</th>
                          <th className="text-right py-3 px-4 font-medium text-gray-600">ยอดขาย</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">จำนวนบิล</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesData.salesmen.map((salesman, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-4 text-center">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-medium ${getRankColor(salesman.rank)}`}>
                                {salesman.rank}
                              </span>
                            </td>
                            <td className="py-2 px-4">
                              <div>
                                <div className="font-medium text-gray-900 text-base">{salesman.salesman_code}</div>
                                {salesmanNames[salesman.salesman_code] && (
                                  <div className="text-sm text-gray-500">{salesmanNames[salesman.salesman_code]}</div>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">{salesman.team_name || '-'}</td>
                            <td className="py-2 px-4 text-right font-medium text-gray-900 text-base">
                              {formatCurrency(salesman.total_amount_before_vat)}
                            </td>
                            <td className="py-2 px-4 text-center text-gray-600 text-base">
                              {salesman.total_bills}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {salesData.metadata && (
                <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
                  <p>เวลาในการค้นหา: {salesData.metadata.query_time} ms</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesOverviewComponent;